import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Grid,
  Paper,
  Alert,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon, Lock as LockIcon } from '@mui/icons-material';
import { api } from '../utils/api';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const UserForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEdit);
  const [sites, setSites] = useState([]);
  const [userSites, setUserSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [twoFASecret, setTwoFASecret] = useState('');
  const [twoFAQRCode, setTwoFAQRCode] = useState('');
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const { isAdmin, isManager, canAccessAnalyzer, canAccessCloudflare, user: currentUser } = useAuth();
  const [adminResetOpen, setAdminResetOpen] = useState(false);
  const [adminResetToken, setAdminResetToken] = useState('');
  const [adminResetLoading, setAdminResetLoading] = useState(false);
  
  // Check if manager is editing their own profile
  const isEditingSelf = isEdit && currentUser && parseInt(id) === currentUser.id;
  
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm({
    defaultValues: {
      name: '',
      email: '',
      role: 'user',
      active: true,
      allowSkPurge: false,
      allowDomainAnalyzer: false,
      allowCloudflare: false,
    }
  });

  const watchedRole = watch('role');
  const [siteQuery, setSiteQuery] = useState('');
  const [sitesPage, setSitesPage] = useState(1);
  const [sitesHasMore, setSitesHasMore] = useState(false);
  const [sitesLoadingMore, setSitesLoadingMore] = useState(false);
  const SITES_PAGE_SIZE = 50;

  const loadSitesForUser = useCallback(async (opts = {}) => {
    if (!id) return;
    const { reset = false, query, page } = opts;
    try {
      if (reset) {
        setSitesLoading(true);
        setSitesPage(1);
      } else {
        setSitesLoadingMore(true);
      }
      const pageToLoad = reset ? 1 : (page ?? sitesPage);
      const q = query ?? '';
      const resp = await api.sites.listForUser(id, { q, page: pageToLoad, limit: SITES_PAGE_SIZE });
      const fetchedSites = resp?.sites || [];
      // Merge or replace sites list
      setSites(prev => reset ? fetchedSites : [...prev, ...fetchedSites]);
      // Build/merge selected site ids from assigned flag
      const assignedIds = fetchedSites.filter(s => s.assigned).map(s => s.id);
      setUserSites(prev => {
        if (reset) {
          return assignedIds;
        }
        const set = new Set(prev);
        assignedIds.forEach(idVal => set.add(idVal));
        return Array.from(set);
      });
      // Pagination flags
      const pagination = resp?.pagination || {};
      const totalPages = pagination.totalPages || 1;
      const currentPage = pagination.page || pageToLoad;
      setSitesHasMore(currentPage < totalPages);
      if (!reset) {
        setSitesPage(currentPage);
      }
    } catch (error) {
      // ignore, surfaced elsewhere if needed
    } finally {
      setSitesLoading(false);
      setSitesLoadingMore(false);
    }
  }, [id]);

  const loadUser = useCallback(async () => {
    try {
      setInitialLoading(true);
      const users = await api.users.list();
      const user = users.find(u => u.id === parseInt(id));
      
      if (!user) {
        toast.error('User not found');
        navigate('/users');
        return;
      }

      // Prevent managers from editing other managers
      if (isManager && !isAdmin && user.role === 'manager' && user.id !== currentUser.id) {
        toast.error('Managers cannot edit other managers');
        navigate('/users');
        return;
      }
      
      setValue('name', user.name);
      setValue('email', user.email);
      setValue('role', user.role);
      setValue('active', user.active);
      setValue('allowSkPurge', !!user.allow_sk_purge);
      setValue('allowDomainAnalyzer', !!user.allow_domain_analyzer);
      setValue('allowCloudflare', !!user.allow_cloudflare);
      
      // Load user's site permissions list for assignment (paginated)
      if (isAdmin || isManager) {
        await loadSitesForUser({ reset: true, query: '' });
      }
    } catch (error) {
      toast.error('Failed to load user');
      navigate('/users');
    } finally {
      setInitialLoading(false);
    }
  }, [id, setValue, isAdmin, isManager, navigate, loadSitesForUser, currentUser?.id]);

  // Debounced search reload
  useEffect(() => {
    if (!(isAdmin || isManager) || !isEdit) return;
    const t = setTimeout(() => {
      // Reset and load first page for new query
      loadSitesForUser({ reset: true, query: siteQuery });
    }, 300);
    return () => clearTimeout(t);
  }, [siteQuery, isAdmin, isManager, isEdit, loadSitesForUser]);

  useEffect(() => {
    if (isEdit) {
      loadUser();
    }
  }, [id, isEdit, isAdmin, isManager, loadUser]);

  const handleSiteToggle = async (siteId, isAssigned) => {
    // Prevent users from removing their own site permissions
    if (!isAssigned && isEditingSelf) {
      toast.error('You cannot remove your own site permissions');
      return;
    }

    try {
      if (isAssigned) {
        await api.sites.addPermission(siteId, parseInt(id));
        setUserSites(prev => [...prev, siteId]);
        toast.success('Site assigned successfully');
      } else {
        await api.sites.removePermission(siteId, parseInt(id));
        setUserSites(prev => prev.filter(id => id !== siteId));
        toast.success('Site removed successfully');
      }
    } catch (error) {
      toast.error(`Failed to ${isAssigned ? 'assign' : 'remove'} site`);
    }
  };

  const generateRandomPassword = () => {
    // Generate a secure random password with required complexity
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const special = '!@#$%^&*';
    
    let password = '';
    
    // Ensure at least one character from each required category
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += special[Math.floor(Math.random() * special.length)];
    
    // Fill the rest with random characters
    const allChars = lowercase + uppercase + numbers + special;
    for (let i = 4; i < 12; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }
    
    // Shuffle the password
    password = password.split('').sort(() => Math.random() - 0.5).join('');
    
    return password;
  };

  const handlePasswordReset = async () => {
    setResetLoading(true);
    try {
      // Generate a new random password
      const newPassword = generateRandomPassword();
      setResetPassword(newPassword);
      
      // Copy to clipboard
      await navigator.clipboard.writeText(newPassword);
      toast.success('New password generated and copied to clipboard!');
    } catch (error) {
      toast.error('Failed to generate password');
    } finally {
      setResetLoading(false);
    }
  };

  // 2FA Management Functions
  const handle2FASetup = async () => {
    try {
      toast.loading('Setting up 2FA...');
      const response = await api.auth.setup2FA();
      toast.dismiss();
      
      // Store 2FA data and show setup UI
      setTwoFASecret(response.secret);
      setTwoFAQRCode(response.qrCode);
      setShow2FASetup(true);
      toast.success('2FA setup completed - scan the QR code');
    } catch (error) {
      toast.dismiss();
      toast.error(error.message || 'Failed to setup 2FA');
    }
  };

  const handle2FAEnable = async () => {
    try {
      const token = prompt('Enter the 2FA token from your authenticator app:');
      if (!token) return;
      
      toast.loading('Enabling 2FA...');
      await api.auth.enable2FA(token);
      toast.dismiss();
      
      // Hide setup UI and show success
      setShow2FASetup(false);
      setTwoFAEnabled(true);
      toast.success('2FA enabled successfully!');
    } catch (error) {
      toast.dismiss();
      toast.error(error.message || 'Failed to enable 2FA');
    }
  };

  const handle2FADisable = async () => {
    try {
      const token = prompt('Enter the 2FA token from your authenticator app:');
      if (!token) return;
      
      toast.loading('Disabling 2FA...');
      await api.auth.disable2FA(token);
      toast.dismiss();
      toast.success('2FA disabled successfully');
    } catch (error) {
      toast.dismiss();
      toast.error(error.message || 'Failed to disable 2FA');
    }
  };

  // Admin-only: Reset another user's 2FA (modal flow)
  const handleAdminReset2FA = () => {
    setAdminResetToken('');
    setAdminResetOpen(true);
  };

  const handleConfirmAdminReset2FA = async () => {
    if (!adminResetToken) {
      toast.error('Please enter your 2FA token');
      return;
    }
    try {
      setAdminResetLoading(true);
      const result = await api.auth.adminReset2FA(id, adminResetToken);
      setAdminResetOpen(false);
      setAdminResetToken('');
      toast.success(result?.message || '2FA reset successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to reset 2FA');
    } finally {
      setAdminResetLoading(false);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const userData = { ...data };
      
      // Remove password if empty in edit mode
      if (isEdit && !userData.password) {
        delete userData.password;
      }

      // If we have a generated password for reset, include it in the update
      if (isEdit && resetPassword && !isEditingSelf) {
        userData.newPassword = resetPassword;
        userData.confirmPassword = resetPassword;
        // Clear the reset password after using it
        setResetPassword('');
      }

      if (isEdit) {
        // Map UI flags to backend fields (permission checks handled server-side)
        if (userData.allowSkPurge !== undefined) {
          userData.allow_sk_purge = !!userData.allowSkPurge;
          delete userData.allowSkPurge;
        }
        if (userData.allowDomainAnalyzer !== undefined) {
          userData.allow_domain_analyzer = !!userData.allowDomainAnalyzer;
          delete userData.allowDomainAnalyzer;
        }
        if (userData.allowCloudflare !== undefined) {
          userData.allow_cloudflare = !!userData.allowCloudflare;
          delete userData.allowCloudflare;
        }
        await api.users.update(id, userData);
        const message = resetPassword ? 'User updated successfully with new password!' : 'User updated successfully';
        toast.success(message);
      } else {
        await api.users.create(userData);
        toast.success('User created successfully');
      }
      
      // Clear the reset password after successful submission
      setResetPassword('');
      navigate('/users');
    } catch (error) {
      toast.error(error.message || `Failed to ${isEdit ? 'update' : 'create'} user`);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ProtectedRoute requireManagerOrAdmin>
      <Box>
        <Box display="flex" alignItems="center" mb={3}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/users')}
            sx={{ mr: 2 }}
          >
            Back to Users
          </Button>
          <Typography variant="h4" fontWeight={600}>
            {isEdit ? 'Edit User' : 'Add New User'}
          </Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box component="form" onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Full Name"
                        placeholder="John Doe"
                        autoComplete="off"
                        inputProps={{ autoComplete: 'off', 'data-lpignore': 'true', 'data-1p-ignore': 'true' }}
                        {...register('name', {
                          required: 'Name is required',
                          minLength: {
                            value: 2,
                            message: 'Name must be at least 2 characters',
                          },
                        })}
                        error={!!errors.name}
                        helperText={errors.name?.message}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        type="email"
                        placeholder="john.doe@company.com"
                        autoComplete="off"
                        inputProps={{ autoComplete: 'off', 'data-lpignore': 'true', 'data-1p-ignore': 'true' }}
                        {...register('email', {
                          required: 'Email is required',
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'Invalid email address',
                          },
                        })}
                        error={!!errors.email}
                        helperText={errors.email?.message || (isEditingSelf && !isAdmin ? 'Only admins can change email addresses' : '')}
                        disabled={isEditingSelf && !isAdmin}
                      />
                    </Grid>

                    {/* Password field only shown for new users */}
                    {!isEdit && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Password"
                          type="password"
                          placeholder="Enter password"
                          autoComplete="new-password"
                          inputProps={{ autoComplete: 'new-password', 'data-lpignore': 'true', 'data-1p-ignore': 'true' }}
                          {...register('password', {
                            required: 'Password is required',
                            minLength: {
                              value: 8,
                              message: 'Password must be at least 8 characters',
                            },
                            pattern: {
                              value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                              message: 'Password must contain at least one lowercase letter, one uppercase letter, and one number',
                            },
                          })}
                          error={!!errors.password}
                          helperText={errors.password?.message}
                        />
                      </Grid>
                    )}

                    {/* Password Reset Section - Only for Admins and Managers editing other users */}
                    {isEdit && !isEditingSelf && (
                      <Grid item xs={12}>
                        <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fafafa' }}>
                          <Typography variant="h6" gutterBottom>
                            Reset Password
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Generate a new secure random password for this user. The password will be copied to your clipboard and immediately applied when you click "Update User".
                          </Typography>
                          
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={8}>
                              <TextField
                                fullWidth
                                label="Generated Password"
                                value={resetPassword}
                                placeholder="Click 'Generate Password' to create a new password"
                                autoComplete="off"
                                inputProps={{ autoComplete: 'off', 'data-lpignore': 'true', 'data-1p-ignore': 'true' }}
                                InputProps={{
                                  readOnly: true,
                                  endAdornment: resetPassword && (
                                    <Button
                                      size="small"
                                      onClick={() => {
                                        navigator.clipboard.writeText(resetPassword);
                                        toast.success('Password copied to clipboard!');
                                      }}
                                      sx={{ minWidth: 'auto', px: 1 }}
                                    >
                                      Copy
                                    </Button>
                                  )
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Button
                                variant="outlined"
                                onClick={handlePasswordReset}
                                disabled={resetLoading}
                                startIcon={resetLoading ? <CircularProgress size={20} /> : <LockIcon />}
                                fullWidth
                              >
                                {resetLoading ? 'Resetting...' : 'Generate Password'}
                              </Button>
                            </Grid>
                          </Grid>
                          
                          {resetPassword && (
                            <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                              <Typography variant="body2" color="success.contrastText">
                                ✅ Password generated and copied to clipboard! Click 'Update User' below to apply the changes.
                              </Typography>
                            </Box>
                          )}
                          
                          <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                            <Typography variant="body2" color="info.contrastText">
                              💡 <strong>How it works:</strong> Click "Generate Password" to create a secure random password (12 characters with uppercase, lowercase, numbers, and special characters). 
                              The password is automatically copied to your clipboard. Then click "Update User" to apply the password change.
                              The user will need to change this password on their next login.
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    )}

                    {/* 2FA Setup Section - Only visible when admin edits their own profile */}
                    {isEdit && isEditingSelf && isAdmin && (
                      <Grid item xs={12}>
                        <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fafafa' }}>
                          <Typography variant="h6" gutterBottom>
                            🔐 Two-Factor Authentication (2FA)
                          </Typography>
                          
                          {!show2FASetup ? (
                            <>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Manage your 2FA settings. Setup generates QR codes for authenticator apps. Enable activates 2FA after verification.
                              </Typography>
                              
                              <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}>
                                  <Button
                                    variant="outlined"
                                    color="primary"
                                    fullWidth
                                    onClick={() => handle2FASetup()}
                                    startIcon={<LockIcon />}
                                  >
                                    Setup 2FA
                                  </Button>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                  <Button
                                    variant="outlined"
                                    color="success"
                                    fullWidth
                                    onClick={() => handle2FAEnable()}
                                    startIcon={<LockIcon />}
                                  >
                                    Enable 2FA
                                  </Button>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                  <Button
                                    variant="outlined"
                                    color="warning"
                                    fullWidth
                                    onClick={() => handle2FADisable()}
                                    startIcon={<LockIcon />}
                                  >
                                    Disable 2FA
                                  </Button>
                                </Grid>
                              </Grid>
                            </>
                          ) : (
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h6" gutterBottom color="primary">
                                📱 Scan QR Code with Authenticator App
                              </Typography>
                              
                              <Box sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
                                <img 
                                  src={twoFAQRCode} 
                                  alt="2FA QR Code" 
                                  style={{ maxWidth: '200px', border: '1px solid #ddd' }}
                                />
                              </Box>
                              
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                <strong>Secret:</strong> {twoFASecret}
                              </Typography>
                              
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                After scanning the QR code, enter the 6-digit token from your authenticator app to enable 2FA.
                              </Typography>
                              
                              <Button
                                variant="contained"
                                color="success"
                                size="large"
                                onClick={() => handle2FAEnable()}
                                startIcon={<LockIcon />}
                                sx={{ mr: 2 }}
                              >
                                Enable 2FA
                              </Button>
                              
                              <Button
                                variant="outlined"
                                color="secondary"
                                onClick={() => setShow2FASetup(false)}
                              >
                                Cancel Setup
                              </Button>
                            </Box>
                          )}
                          
                          <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                            <Typography variant="body2" color="info.contrastText">
                              💡 <strong>2FA Management:</strong> Setup generates QR codes for authenticator apps. Enable activates 2FA after verification. 
                              Disable removes 2FA protection. All users must have 2FA enabled for security.
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    )}

                    {/* Admin-only: Reset 2FA when editing another user */}
                    {isEdit && isAdmin && !isEditingSelf && (
                      <Grid item xs={12}>
                        <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fafafa' }}>
                          <Typography variant="h6" gutterBottom>
                            🔐 2FA Administration
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Admins can reset a user\'s 2FA in emergencies. This clears their current 2FA and issues a new setup on next login.
                          </Typography>
                          <Button
                            variant="outlined"
                            color="warning"
                            onClick={handleAdminReset2FA}
                            startIcon={<LockIcon />}
                          >
                            Reset User 2FA
                          </Button>
                        </Box>
                      </Grid>
                    )}

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Role</InputLabel>
                        <Select
                          label="Role"
                          {...register('role', { required: 'Role is required' })}
                          value={watch('role') || 'user'}
                          onChange={(e) => setValue('role', e.target.value)}
                          disabled={isManager && isEditingSelf && !isAdmin}
                        >
                          <MenuItem value="user">User</MenuItem>
                          {/* Managers can only create users with 'user' role, except when editing themselves */}
                          {(isAdmin || (isManager && isEditingSelf)) && <MenuItem value="manager">Manager</MenuItem>}
                          {isAdmin && <MenuItem value="admin">Admin</MenuItem>}
                        </Select>
                        {isManager && isEditingSelf && !isAdmin && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            You cannot change your own role as a manager
                          </Typography>
                        )}
                        {isManager && !isAdmin && !isEditingSelf && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            As a manager, you can only edit users with "user" role
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>

                    {/* Admin-only: Allow surrogate-key purge for USER role */}
                    {isAdmin && (watch('role') === 'user') && (
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={<Checkbox {...register('allowSkPurge')} checked={!!watch('allowSkPurge')} onChange={(e) => setValue('allowSkPurge', e.target.checked)} />}
                          label={
                            <span>
                              Allow surrogate-key purge for this user
                            </span>
                          }
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Grants this USER permission to purge by surrogate keys. Use sparingly.
                        </Typography>
                      </Grid>
                    )}

                    {/* Domain Analyzer access delegation:
                        - Admins can grant to managers and users
                        - Managers (with analyzer access) can grant to users */}
                    {(isAdmin || (isManager && canAccessAnalyzer && watch('role') === 'user')) && (watch('role') !== 'admin') && (
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={<Checkbox {...register('allowDomainAnalyzer')} checked={!!watch('allowDomainAnalyzer')} onChange={(e) => setValue('allowDomainAnalyzer', e.target.checked)} />}
                          label={
                            <span>
                              Allow Domain Analyzer access
                            </span>
                          }
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Grants access to the Domain Analyzer for ISP blocking detection.
                          {isAdmin && watch('role') === 'manager' && ' This manager will also be able to grant access to users.'}
                        </Typography>
                      </Grid>
                    )}

                    {(isAdmin || (isManager && canAccessCloudflare && watch('role') === 'user')) && (watch('role') !== 'admin') && (
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={<Checkbox {...register('allowCloudflare')} checked={!!watch('allowCloudflare')} onChange={(e) => setValue('allowCloudflare', e.target.checked)} />}
                          label={
                            <span>
                              Allow Cloudflare Purge access
                            </span>
                          }
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Grants access to the Cloudflare cache purge section in the Dashboard.
                          {isAdmin && watch('role') === 'manager' && ' This manager will also be able to grant access to users.'}
                        </Typography>
                      </Grid>
                    )}

                                          {isEdit && (
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <InputLabel>Status</InputLabel>
                          <Select
                            label="Status"
                            {...register('active')}
                            defaultValue={true}
                            disabled={isManager && isEditingSelf && !isAdmin}
                          >
                            <MenuItem value={true}>Active</MenuItem>
                            <MenuItem value={false}>Inactive</MenuItem>
                          </Select>
                          {isManager && isEditingSelf && !isAdmin && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                              You cannot deactivate yourself as a manager
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                      >
                        {loading ? 'Saving...' : (isEdit ? 'Update User' : 'Create User')}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </Grid>



          {/* Site Assignment - Show for Admins and Managers editing Users/Managers */}
          {(isAdmin || isManager) && isEdit && (watchedRole === 'user' || watchedRole === 'manager') && (
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Site Access Management
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Assign this {watchedRole} to specific sites. They will be able to purge cache for assigned sites.
                    {isManager && !isAdmin && ' (You can only assign sites you have access to)'}
                  </Typography>
                  
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search sites by domain or name"
                    value={siteQuery}
                    onChange={(e) => {
                      setSiteQuery(e.target.value);
                    }}
                    autoComplete="off"
                    inputProps={{ autoComplete: 'off', 'data-lpignore': 'true', 'data-1p-ignore': 'true' }}
                    sx={{ mb: 2 }}
                  />
                  {sitesLoading ? (
                    <Box display="flex" justifyContent="center" p={3}>
                      <CircularProgress />
                    </Box>
                  ) : sites.length === 0 ? (
                    <Alert severity="info">
                      {siteQuery
                        ? `No sites matching "${siteQuery}". Try a different search term.`
                        : 'No sites available. Create sites first to assign them to users.'}
                    </Alert>
                  ) : (
                    <>
                      <List>
                        {sites.map((site, index) => (
                          <div key={site.id}>
                            <ListItem>
                              <ListItemText
                                primary={site.domain}
                                secondary={`Related Service: ${site.name}`}
                              />
                              <ListItemSecondaryAction>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={userSites.includes(site.id)}
                                      onChange={(e) => handleSiteToggle(site.id, e.target.checked)}
                                      disabled={isEditingSelf && userSites.includes(site.id)}
                                    />
                                  }
                                  label={
                                    userSites.includes(site.id) 
                                      ? (isEditingSelf ? 'Assigned (Cannot remove own access)' : 'Assigned')
                                      : 'Not Assigned'
                                  }
                                />
                              </ListItemSecondaryAction>
                            </ListItem>
                            {index < sites.length - 1 && <Divider />}
                          </div>
                        ))}
                      </List>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        {sitesHasMore ? (
                          <Button
                            variant="outlined"
                            onClick={() => {
                              if (sitesLoadingMore) return;
                              const nextPage = sitesPage + 1;
                              setSitesPage(nextPage);
                              loadSitesForUser({ reset: false, query: siteQuery, page: nextPage });
                            }}
                            disabled={sitesLoadingMore}
                          >
                            {sitesLoadingMore ? 'Loading…' : 'Load More'}
                          </Button>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            End of list
                          </Typography>
                        )}
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                User Roles
              </Typography>
              
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Admin:</strong> Full access - can import Fastly services, manage sites, 
                  users, and view all audit logs. Has access to all purge operations.
                </Typography>
              </Alert>
              
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Manager:</strong> Can create user accounts and assign them to sites they have access to. 
                  Cannot import services from Fastly or create other managers/admins. Can purge cache for assigned sites.
                </Typography>
              </Alert>
              
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>User:</strong> Can only purge cache for sites they have been 
                  given permission to access. Cannot manage sites or users.
                </Typography>
              </Alert>

              {watchedRole === 'manager' && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Warning:</strong> Manager users can manage sites and users, 
                    but cannot import services from Fastly.
                  </Typography>
                </Alert>
              )}

              {watchedRole === 'admin' && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Warning:</strong> Admin users have full access to all 
                    features including Fastly service imports and system settings.
                  </Typography>
                </Alert>
              )}

              {isManager && !isAdmin && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Note:</strong> As a Manager, you can only create users with "User" role and assign them sites you have access to. 
                    {isEditingSelf 
                      ? 'You cannot change your own Manager role or deactivate yourself to prevent losing access to management features.' 
                      : 'Only Admins can create other Admins or Managers.'
                    }
                  </Typography>
                </Alert>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
      {/* Admin Reset 2FA Dialog */}
      <Dialog open={adminResetOpen} onClose={() => setAdminResetOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm 2FA Reset</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter your admin 2FA token to confirm resetting this user's 2FA.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Your 2FA Token"
            value={adminResetToken}
            onChange={(e) => setAdminResetToken(e.target.value)}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdminResetOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleConfirmAdminReset2FA}
            disabled={adminResetLoading}
            startIcon={adminResetLoading ? <CircularProgress size={18} /> : <LockIcon />}
          >
            {adminResetLoading ? 'Resetting…' : 'Confirm Reset'}
          </Button>
        </DialogActions>
      </Dialog>
    </ProtectedRoute>
  );
};

export default UserForm;
